import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PERFIL_OFERTAS_COPY, formatDineroCop } from '../../data/perfil-ofertas.content';
import { MicroempresarioService } from '../../services/microempresario.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import { ModalService } from 'src/app/shared/service/modal.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';
import { esOfertaBancoldex } from './ofertas-filtros.util';
import {
  OfertaCondicionFinancieraMock,
  OfertaDocGuiaMock,
  OfertaDocSegmentoMock,
  OfertaMotivoRechazoMock,
  OfertaPropuestaMock,
} from './perfil-ofertas.model';

export type OfertasModalId =
  | 'salir'
  | 'garantias'
  | 'tomar1'
  | 'tomar2'
  | 'rechazar'
  | 'comparar'
  | null;

const MOTIVO_CONDICIONES = 2;
const MOTIVO_OTRO = 4;

@Component({
  selector: 'app-perfil-ofertas-modales',
  templateUrl: './perfil-ofertas-modales.component.html',
  styleUrls: ['./perfil-ofertas-modales.component.scss'],
  standalone: false,
})
export class PerfilOfertasModalesComponent implements OnChanges {
  @Input() open: OfertasModalId = null;
  @Input() diasRestantes: number | null = 8;
  @Input() oferta: OfertaPropuestaMock | null = null;
  @Input() seleccionadas: OfertaPropuestaMock[] = [];
  @Input() par078 = PERFIL_OFERTAS_COPY.par078Fallback;
  @Input() par079 = PERFIL_OFERTAS_COPY.par079Fallback;
  @Input() idSolicitud = '';
  @Input() motivos: OfertaMotivoRechazoMock[] = [];
  @Input() enviando = false;

  @Output() closed = new EventEmitter<void>();
  @Output() salir = new EventEmitter<void>();
  @Output() revisar = new EventEmitter<void>();
  @Output() tomarSiguiente = new EventEmitter<void>();
  @Output() tomarConfirmar = new EventEmitter<void>();
  @Output() rechazar = new EventEmitter<{
    idMotivo: number;
    detalle: string;
    condiciones: number[];
  }>();
  @Output() quitarComparar = new EventEmitter<number>();
  @Output() tomarDesdeComparar = new EventEmitter<OfertaPropuestaMock>();

  readonly copy = PERFIL_OFERTAS_COPY;
  readonly assetArchivo = 'assets/img/perfil/icon-archivo-cargado.svg';
  readonly motivoCondiciones = MOTIVO_CONDICIONES;
  readonly motivoOtro = MOTIVO_OTRO;

  condicionesCatalogo: OfertaCondicionFinancieraMock[] = [];
  docsSegmentoCatalogo: OfertaDocSegmentoMock[] = [];

  acepta078 = false;
  idMotivo = 0;
  detalle = '';
  condiciones = new Set<number>();
  /** id tipo doc → { nombre, idDocumentosSegmento? } */
  docsCargados = new Map<number, { nombre: string; idDocumentosSegmento?: number }>();
  docsCargando = false;

  constructor(
    private readonly modalService: ModalService,
    private readonly microempresario: MicroempresarioService,
    private readonly parametros: ParametrosService,
    private readonly sweetAlert: SweetAlertService,
    private readonly analyticsTag: AnalyticsTagService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open === 'tomar2') {
      this.prepararDocsSegmento();
    }
  }

  get motivosLista(): OfertaMotivoRechazoMock[] {
    return this.motivos;
  }

  get dias(): number {
    return this.diasRestantes ?? 8;
  }

  get puedeRechazar(): boolean {
    if (this.enviando || !this.idMotivo) {
      return false;
    }
    if (this.idMotivo === MOTIVO_OTRO && !this.detalle.trim()) {
      return false;
    }
    if (this.idMotivo === MOTIVO_CONDICIONES && this.condiciones.size === 0) {
      return false;
    }
    return true;
  }

  get requiereDocsSegmento(): boolean {
    return !!this.oferta?.idSegmento;
  }

  get docsObligatoriosListos(): boolean {
    if (!this.requiereDocsSegmento) {
      return true;
    }
    if (this.docsCargando) {
      return false;
    }
    return this.docsSegmentoCatalogo
      .filter((d) => d.obligatorio)
      .every((d) => this.docsCargados.has(d.id));
  }

  get puedeConfirmarTomar(): boolean {
    return !this.enviando && this.docsObligatoriosListos;
  }

  /** `onClose` es compartido por varios botones (garantías / cancelar tomar / cancelar rechazo) y por el backdrop. */
  trackModalCta(ctaLabel: string, ctaElementId: string): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel,
      ctaSection: 'ofertas-modal',
      ctaComponent: 'perfil-ofertas-modales',
      ctaElementId,
      hu: '8209',
    });
  }

  onClose(): void {
    this.reset();
    this.closed.emit();
  }

  onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onRevisar(): void {
    this.trackModalCta(this.copy.revisarOfertasCta, 'btn-ofertas-revisar');
    this.onClose();
    this.revisar.emit();
  }

  onSalir(): void {
    this.trackModalCta(this.copy.salir, 'btn-ofertas-salir');
    this.reset();
    this.salir.emit();
  }

  onTomarSiguiente(): void {
    if (!this.acepta078) {
      return;
    }
    this.trackModalCta(this.copy.siTomarOferta, 'btn-ofertas-si-tomar');
    this.tomarSiguiente.emit();
  }

  onTomarConfirmar(): void {
    if (!this.puedeConfirmarTomar) {
      return;
    }
    this.trackModalCta(this.copy.siTomarOferta, 'btn-ofertas-confirmar-tomar');
    this.tomarConfirmar.emit();
  }

  onRechazar(): void {
    if (!this.puedeRechazar) {
      return;
    }
    this.trackModalCta(this.copy.rechazarOfertas, 'btn-ofertas-rechazar-confirmar');
    this.rechazar.emit({
      idMotivo: this.idMotivo,
      detalle: this.detalle.trim(),
      condiciones: [...this.condiciones],
    });
  }

  onMotivoChange(value: number): void {
    this.idMotivo = value;
    this.condiciones = new Set();
    this.detalle = '';
    if (this.idMotivo === MOTIVO_CONDICIONES) {
      this.cargarCondiciones();
    } else {
      this.condicionesCatalogo = [];
    }
  }

  toggleCondicion(id: number): void {
    if (this.condiciones.has(id)) {
      this.condiciones.delete(id);
    } else {
      this.condiciones.add(id);
    }
  }

  docCargado(id: number): boolean {
    return this.docsCargados.has(id);
  }

  nombreDocCargado(id: number): string {
    return this.docsCargados.get(id)?.nombre ?? '';
  }

  onDocSelected(doc: OfertaDocSegmentoMock, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.subirDocSegmento(doc, file, input);
  }

  onDocQuitar(id: number): void {
    const cargado = this.docsCargados.get(id);
    if (!cargado?.idDocumentosSegmento) {
      this.docsCargados.delete(id);
      return;
    }
    this.microempresario.deleteFileSegmento(String(cargado.idDocumentosSegmento)).subscribe({
      next: (data: { exitoso?: boolean; error?: string }) => {
        if (data?.exitoso) {
          this.docsCargados.delete(id);
        } else {
          void this.sweetAlert.errorModal('Error!', true, data?.error || 'No se pudo eliminar el documento');
        }
      },
      error: () => {
        void this.sweetAlert.errorModal('Error!', true, 'No se pudo eliminar el documento');
      },
    });
  }

  onDocVer(id: number): void {
    const cargado = this.docsCargados.get(id);
    if (!cargado?.idDocumentosSegmento) {
      return;
    }
    this.microempresario.downloadFileSegmento(String(cargado.idDocumentosSegmento)).subscribe({
      next: (response: { mimeType?: string; data?: string }) => {
        if (!response?.data || !response?.mimeType) {
          return;
        }
        const mime = response.mimeType;
        const isImage = mime.split('/')[0] === 'image';
        if (mime === 'application/pdf' || isImage) {
          const blob = new Blob([this.base64ToBytes(response.data)], { type: mime });
          window.open(URL.createObjectURL(blob), '_blank');
          return;
        }
        const a = document.createElement('a');
        a.href = `data:${mime};base64,${response.data}`;
        a.download = cargado.nombre || 'documento';
        a.click();
      },
      error: () => {
        void this.sweetAlert.errorModal('Error!', true, 'No se pudo descargar el documento');
      },
    });
  }

  abrirAuthBancoldex(): void {
    void this.modalService.enlaceModal(this.copy.docsSegmentoAuthUrl);
  }

  onDescargarGuia(guia: OfertaDocGuiaMock): void {
    if (!guia?.archivo) {
      return;
    }
    void this.modalService.descargarGuiaModal(guia.archivo, guia.nombre, guia.tipoContenido);
  }

  iconoGuia(extension: string): string {
    const ext = (extension || '').toLowerCase();
    if (ext === 'pdf') {
      return 'picture_as_pdf';
    }
    if (ext === 'xls' || ext === 'xlsx') {
      return 'table_view';
    }
    if (ext === 'doc' || ext === 'docx') {
      return 'description';
    }
    return 'insert_drive_file';
  }

  tipoIconoGuia(extension: string): 'pdf' | 'excel' | 'word' | 'file' {
    const ext = (extension || '').toLowerCase();
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (ext === 'xls' || ext === 'xlsx') {
      return 'excel';
    }
    if (ext === 'doc' || ext === 'docx') {
      return 'word';
    }
    return 'file';
  }

  dinero(valor: number): string {
    return formatDineroCop(valor);
  }

  tasaCompare(tasa: number): string {
    return `${tasa}% E.A`;
  }

  dineroCargo(valor: number): string {
    return `-$${Math.round(Math.abs(valor)).toLocaleString('es-CO')}`;
  }

  filasTomar(oferta: OfertaPropuestaMock): { icon: string; label: string; value: string }[] {
    return [
      { icon: '💰', label: this.copy.filaTomarMonto, value: this.dinero(oferta.monto) },
      { icon: '📊', label: this.copy.filaTomarGarantias, value: this.dineroCargo(this.totalGarantias(oferta)) },
      { icon: '🧾', label: this.copy.filaTomarDesembolso, value: this.dinero(oferta.desembolsoAproximado) },
      { icon: '📈', label: this.copy.filaTomarTasa, value: this.tasaCompare(oferta.tasa) },
      { icon: '💵', label: this.copy.filaTomarCuota, value: this.dinero(oferta.cuota) },
      { icon: '🗓️', label: this.copy.filaTomarPlazo, value: this.copy.plazoMeses(oferta.plazo) },
    ];
  }

  totalGarantias(oferta: OfertaPropuestaMock): number {
    return (oferta.neoCargoComisiones ?? []).reduce((sum, c) => sum + (c.monto ?? 0), 0);
  }

  esBancoldex(oferta: OfertaPropuestaMock): boolean {
    return esOfertaBancoldex(oferta);
  }

  /** Tras éxito de API, el padre cierra y limpia. */
  resetTrasExito(): void {
    this.reset();
  }

  private prepararDocsSegmento(): void {
    this.docsCargados = new Map();
    this.docsSegmentoCatalogo = [];
    if (!this.requiereDocsSegmento) {
      return;
    }
    this.cargarDocsSegmentoApi();
  }

  private cargarDocsSegmentoApi(): void {
    const idSegmento = this.oferta?.idSegmento;
    if (!idSegmento || !this.idSolicitud) {
      return;
    }
    this.docsCargando = true;
    this.microempresario.getDocumentosBySegmento(idSegmento).subscribe({
      next: (resp: { exitoso?: boolean; data?: { tipoDocumento?: any[] } }) => {
        const tipos = resp?.exitoso ? resp.data?.tipoDocumento ?? [] : [];
        this.docsSegmentoCatalogo = tipos.map((t: any) => this.mapTipoDoc(t));
        this.cargarDocsYaSubidos(idSegmento);
      },
      error: () => {
        this.docsCargando = false;
        this.docsSegmentoCatalogo = [];
        void this.sweetAlert.errorModal('Error!', true, 'No se pudieron cargar los documentos del segmento');
      },
    });
  }

  private cargarDocsYaSubidos(idSegmento: number): void {
    this.microempresario.getDocumentosBySolicitudAndSegmento(idSegmento, this.idSolicitud).subscribe({
      next: (data: { documentosSegmentoSolicutd?: any[] }) => {
        const docs = data?.documentosSegmentoSolicutd ?? [];
        for (const item of docs) {
          const tipoId = Number(item.idTipoDocumentoSegmento);
          if (!tipoId) {
            continue;
          }
          this.docsCargados.set(tipoId, {
            nombre: item.nombreArchivo || item.nombreDocumento || 'Archivo',
            idDocumentosSegmento: item.idDocumentosSegmento,
          });
        }
        this.docsCargando = false;
      },
      error: () => {
        this.docsCargando = false;
      },
    });
  }

  private mapTipoDoc(tipo: any): OfertaDocSegmentoMock {
    const seg = tipo?.segTipoDocumento;
    const exts: any[] = seg?.segTipoDocExtensions ?? [];
    const accept = exts
      .filter((e) => e?.estado === 'HABILITADO')
      .map((e) => `.${String(e.extension || '').toLowerCase()}`)
      .join(',');
    const guias = (seg?.segDocumentoGuias ?? [])
      .filter((g: any) => String(g?.estado ?? '').toUpperCase() === 'HABILITADO' && g?.archivo)
      .map((g: any) => ({
        nombre: g.nombre || 'Guía',
        extension: String(g.extension || '').toLowerCase(),
        tipoContenido: g.tipoContenido || 'application/octet-stream',
        archivo: g.archivo,
        guia: g.guia || null,
      }));
    return {
      id: Number(tipo.id),
      nombre: seg?.nombre || tipo.nombre || 'Documento',
      obligatorio: tipo.obligatorio !== false,
      accept: accept || '.pdf,.jpg,.jpeg,.png',
      guias,
    };
  }

  private subirDocSegmento(doc: OfertaDocSegmentoMock, file: File, input: HTMLInputElement): void {
    const idSegmento = this.oferta?.idSegmento;
    if (!idSegmento) {
      return;
    }
    const parts = file.name.split('.');
    const extension = (parts.pop() || '').toLowerCase();
    const allowed = (doc.accept || '')
      .split(',')
      .map((e) => e.trim().replace(/^\./, '').toLowerCase())
      .filter(Boolean);
    if (allowed.length && !allowed.includes(extension)) {
      void this.sweetAlert.errorModal(
        'Error!',
        true,
        `Solo son permitidas las siguientes extensiones: ${doc.accept}`
      );
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      const body = {
        idSegmento,
        idTipoDocumentoSegmento: doc.id,
        idSolicitud: this.idSolicitud,
        nombreDocumento: this.normalizeName(doc.nombre),
        nombreArchivo: this.normalizeName(parts.join('.') || file.name),
        tipoContenido: file.type,
        extension,
        archivo: base64,
      };
      this.microempresario.saveFileSegmento(body).subscribe({
        next: (data: { exitoso?: boolean; error?: string; idDocumentoSegmento?: number }) => {
          input.value = '';
          if (data?.exitoso) {
            this.docsCargados.set(doc.id, {
              nombre: file.name,
              idDocumentosSegmento: data.idDocumentoSegmento,
            });
            // Recargar ids reales por si el backend no devolvió id.
            if (!data.idDocumentoSegmento) {
              this.cargarDocsYaSubidos(idSegmento);
            }
          } else {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              data?.error || 'No se pudo subir el archivo'
            );
          }
        },
        error: () => {
          input.value = '';
          void this.sweetAlert.errorModal('Error!', true, 'No se pudo subir el archivo');
        },
      });
    };
    reader.readAsDataURL(file);
  }

  private cargarCondiciones(): void {
    this.parametros.getCondicionesFinancieras().subscribe({
      next: (resp: { data?: any[]; exitoso?: boolean }) => {
        const rows = resp?.data ?? [];
        this.condicionesCatalogo = rows.map((r: any) => ({
          id: Number(r.idCondicionesFinancieras),
          nombre: String(r.nombre || ''),
        }));
      },
      error: () => {
        this.condicionesCatalogo = [];
        void this.sweetAlert.errorModal('Error!', true, 'No se pudieron cargar las condiciones financieras');
      },
    });
  }

  private normalizeName(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ñ/g, 'n')
      .replace(/Ñ/g, 'N')
      .trim()
      .replace(/\s+/g, '_');
  }

  private base64ToBytes(base64: string): ArrayBuffer {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private reset(): void {
    this.acepta078 = false;
    this.idMotivo = 0;
    this.detalle = '';
    this.condiciones = new Set();
    this.condicionesCatalogo = [];
    this.docsCargados = new Map();
    this.docsSegmentoCatalogo = [];
    this.docsCargando = false;
  }
}
